import { useState, Activity, useRef, useCallback } from "react";
import "@css/temp_configure.css";
import useStore from "@store/store.ts";
import { useGroupStore } from "@store/groupStore.ts";
import { useInitializeGroup } from "@hooks/useInitializeGroup.ts";
import { useNavigate } from "react-router-dom";
import NoneConfigData from "@pages/roster-config/components/none-configdata.tsx";
import ConfTab0 from "@components/feature/roster_configure/ConfTab0";
import ConfTab1 from "@components/feature/roster_configure/ConfTab1";
import ConfTab3, {
  type ConfTab3Ref,
} from "@components/feature/roster_configure/ConfTab3";
import CustomModal from "@/components/modal/CustomModal";
import useRosterConfig from "@/hooks/useRosterConfig";
import ConfTab4, {
  type ConfTab4Ref,
} from "@/components/feature/roster_configure/ConfTab4";
import { useIsAdmin } from "@/hooks/useAuth";
import { SubTab, SubTabItem } from "@/layouts/SubTab";

const TABS = [
  {
    label: "근무표 설정",
    value: 0,
  },
  {
    label: "근무코드 설정",
    value: 1,
  },
  {
    label: "주휴 설정",
    value: 3,
  },
  {
    label: "원티드 설정",
    value: 4,
  },
];

function Roster_configure() {
  const { loginUser } = useStore();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const confTab3Ref = useRef<ConfTab3Ref>(null);
  const confTab4Ref = useRef<ConfTab4Ref>(null);

  // 2. 모달 상태와 다음 탭 정보를 위한 state 추가
  const [unSavedModalState, setUnSavedModalState] = useState<{
    isOpen: boolean;
    nextTab: number | null;
  }>({
    isOpen: false,
    nextTab: null,
  });

  // 3. 탭 클릭 핸들러 수정
  const handleTabClick = useCallback(
    (nextTabIndex: number) => {
      // 현재 탭이 3번 탭(주휴 설정)이고, 변경사항이 있다면
      if (currentTab === 3 && confTab3Ref.current?.hasUnsavedChanges) {
        // 모달을 열고, 이동할 탭 정보를 저장
        setUnSavedModalState({ isOpen: true, nextTab: nextTabIndex });
      } else if (currentTab === 4 && confTab4Ref.current?.hasUnsavedChanges) {
        // 현재 탭이 4번 탭(원티드 설정)이고, 변경사항이 있다면
        setUnSavedModalState({ isOpen: true, nextTab: nextTabIndex });
      } else {
        // 그 외의 경우 바로 탭 이동
        setCurrentTab(nextTabIndex);
      }
    },
    [currentTab]
  );

  // 4. 모달의 "저장하고 이동" 버튼 핸들러
  const handleConfirmSaveAndSwitch = () => {
    if (unSavedModalState.nextTab !== null) {
      if (currentTab === 3 && confTab3Ref.current) {
        // ConfTab3의 저장 함수 호출, 성공 시 탭 이동 및 모달 닫기
        confTab3Ref.current.handleSave({
          onSuccess: () => {
            setCurrentTab(unSavedModalState.nextTab!);
            setUnSavedModalState({ isOpen: false, nextTab: null });
          },
        });
      } else if (currentTab === 4 && confTab4Ref.current) {
        // ConfTab4의 저장 함수 호출, 성공 시 탭 이동 및 모달 닫기
        confTab4Ref.current.handleSave({
          onSuccess: () => {
            setCurrentTab(unSavedModalState.nextTab!);
            setUnSavedModalState({ isOpen: false, nextTab: null });
          },
        });
      }
    }
  };

  // 5. 모달 닫기 핸들러
  const handleCloseUnSavedModal = () => {
    // 저장 안 함(취소) 선택 시, 원래 이동하려던 탭으로 이동
    // 상태를 초기화하기 위해 자식 컴포넌트의 reset 함수 호출
    if (currentTab === 3 && confTab3Ref.current) {
      confTab3Ref.current.handleReset();
    } else if (currentTab === 4 && confTab4Ref.current) {
      confTab4Ref.current.handleReset();
    }
    if (unSavedModalState.nextTab !== null) {
      setCurrentTab(unSavedModalState.nextTab);
    }
    setUnSavedModalState({ isOpen: false, nextTab: null });
  };

  // Store
  const { selectedGroupId } = useGroupStore();
  const { groups } = useInitializeGroup({
    loginUser,
    isAdmin,
    navigate,
  });

  const { data: serverConfig, isLoading: isConfigLoading } = useRosterConfig();

  console.log("serverConfig::", serverConfig);

  if (isAdmin && Array.isArray(groups) && groups.length === 0) {
    return <NoneConfigData />;
  }

  return (
    <div className="py-10 ">
      <SubTab aria-label="근무표 설정 탭">
        {TABS.map((tab) => (
          <SubTabItem
            key={tab.value}
            label={tab.label}
            value={tab.value}
            isActive={currentTab === tab.value}
            onSelect={handleTabClick}
          />
        ))}
      </SubTab>

      <div className="flex flex-col gap-10 justify-center items-center pt-5 px-5">
        {/* 근무표 설정 */}
        <Activity mode={currentTab === 0 ? "visible" : "hidden"}>
          <ConfTab0
            isAdmin={isAdmin}
            selectedGroupId={selectedGroupId}
            serverConfig={serverConfig}
            isConfigLoading={isConfigLoading}
          />
        </Activity>

        {/* 근무코드 설정 */}
        <Activity mode={currentTab === 1 ? "visible" : "hidden"}>
          <ConfTab1 isAdmin={isAdmin} selectedGroupId={selectedGroupId} />
        </Activity>

        {/* 주휴 설정 */}
        <Activity mode={currentTab === 3 ? "visible" : "hidden"}>
          {/* TODO : availableShifts 추후 추가예정 */}
          <ConfTab3
            ref={confTab3Ref}
            isAdmin={isAdmin}
            selectedGroupId={selectedGroupId}
            serverConfig={serverConfig}
            visible={currentTab === 3}
          />
        </Activity>

        <Activity mode={currentTab === 4 ? "visible" : "hidden"}>
          <ConfTab4
            ref={confTab4Ref}
            isAdmin={isAdmin}
            selectedGroupId={selectedGroupId}
            serverConfig={serverConfig}
            visible={currentTab === 4}
          />
        </Activity>
      </div>

      {/* 저장되지 않은 변경사항 모달 (탭 전환 감지용)*/}
      <CustomModal.Confirm
        isOpen={unSavedModalState.isOpen}
        onClose={handleCloseUnSavedModal}
        onConfirm={handleConfirmSaveAndSwitch}
        title="현재 설정을 저장하시겠습니까?"
      />
    </div>
  );
}
export default Roster_configure;
